/**
 * features/finance/Discounts.tsx
 * ------------------------------------------------------------------
 * ISSUING A DISCOUNT, THE WAY A RECEIPT IS CAPTURED (the school, Nov 2026).
 *
 * Find the invoice, say how much and why, and it is done: a numbered document,
 * the balance down at once, and the invoice showing the reduced fee. One invoice
 * per discount. It replaces recording a concession on a register and applying it
 * in a second step, which sometimes never happened.
 *
 * It looks like a receipt and is not one. Money did not arrive, so it stays out
 * of cash-up, the cash book and bank reconciliation; see issueDiscount.
 *
 * MORE THAN IS OWED is allowed, and the screen says how much of it becomes credit
 * on the account. More than the fee is not.
 *
 * NEVER EDITED. To change one, reverse it with a reason and issue another. Both
 * stay on the file. Only the bursar, operations admin and system admin may issue
 * or reverse (canIssueDiscounts, and the rules); other finance staff can read.
 *
 * THE ONE PLACE A DISCOUNT IS GIVEN (Nov 2026). The old screen is off the menu and
 * reached from a link here, because it is only a record now. Bursaries and full
 * annual payment no longer have routes of their own: a bursary is a type here.
 * ------------------------------------------------------------------ */
import { useMemo, useState } from 'react';
import { AlertTriangle, BadgePercent, Loader2, Search, Undo2 } from 'lucide-react';
import { Spinner } from '@/components/ui/primitives';
import { useAuth } from '@/contexts/AuthContext';
import { getPermissions } from '@/lib/permissions';
import { useFinanceData } from '@/features/finance/useFinanceData';
import {
  DISCOUNT_KIND_LABELS, feeAfterDiscounts, fmtPula, round2, type DiscountKind, type Invoice,
} from '@/lib/financeCore';
import {
  discountAmount, discountRoom, discountToCredit, type Adjustment,
} from '@/lib/adjustments';
import { issueDiscount, reverseAdjustment } from '@/features/finance/adjustmentWrite';
import { GradientHero, SoftCard, glassFieldClass, btnPrimary, btnGhost } from '@/features/finance/ui';

const KINDS: DiscountKind[] = ['sibling', 'staff', 'bursary', 'prepayment', 'manual'];

export default function Discounts() {
  const { userData, userRoles } = useAuth();
  const mayIssue = getPermissions(userRoles).canIssueDiscounts;
  const { invoices, adjustments, loading } = useFinanceData();
  const actor = { uid: userData?.uid, name: userData?.displayName, email: userData?.email, role: userRoles.join(', ') };

  const [search, setSearch] = useState('');
  const [pickedId, setPickedId] = useState<string | null>(null);
  const [discountKind, setDiscountKind] = useState<DiscountKind>('sibling');
  const [mode, setMode] = useState<'percent' | 'amount'>('amount');
  const [value, setValue] = useState('');
  const [reason, setReason] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<{ ok: boolean; text: string } | null>(null);
  const [reversing, setReversing] = useState<Adjustment | null>(null);
  const [reverseReason, setReverseReason] = useState('');

  // Picked by id, so the figures follow the invoice's live balance.
  const picked: Invoice | null = invoices.find((i) => i.id === pickedId) ?? null;

  const matches = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (q.length < 2) return [] as Invoice[];
    return invoices
      .filter((i) => i.status !== 'void' && discountRoom(i) > 0)
      .filter((i) => `${i.invoiceNo} ${i.studentName ?? ''}`.toLowerCase().includes(q))
      .slice(0, 12);
  }, [invoices, search]);

  const rows = useMemo(
    () => adjustments
      .filter((a) => a.isDiscount)
      .sort((a, b) => String(b.createdAt).localeCompare(String(a.createdAt))),
    [adjustments],
  );

  const amount = picked ? discountAmount(mode, Number(value), picked.total) : 0;
  const room = picked ? discountRoom(picked) : 0;
  const tooMuch = amount > room + 0.005;
  const toCredit = picked ? discountToCredit(amount, picked.balance) : 0;
  const reasonOk = reason.trim().length >= 3;

  async function submit() {
    if (!picked) return;
    setBusy(true);
    setMsg(null);
    try {
      const a = await issueDiscount({ invoice: { id: picked.id }, discountKind, amount, reason, date, actor, entered: { mode, value: Number(value) } });
      setMsg({ ok: true, text: `Discount ${a.adjustmentNo} issued: ${fmtPula(a.amount)} off ${a.invoiceNo}.` });
      setPickedId(null); setValue(''); setReason(''); setSearch('');
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally { setBusy(false); }
  }

  async function doReverse() {
    if (!reversing) return;
    setBusy(true);
    setMsg(null);
    try {
      const r = await reverseAdjustment(reversing, reverseReason, actor);
      setMsg({ ok: true, text: `${reversing.adjustmentNo} reversed by ${r.adjustmentNo}. Both stay on the record.` });
      setReversing(null); setReverseReason('');
    } catch (e) {
      setMsg({ ok: false, text: (e as Error).message });
    } finally { setBusy(false); }
  }

  if (loading) return <div className="flex justify-center py-16"><Spinner label="Loading invoices…" /></div>;

  return (
    <div className="space-y-5">
      <GradientHero icon={BadgePercent} title="Discounts" subtitle="Issue a discount against one invoice. The fee and the balance come down straight away." />

      {msg && (
        <p role="status" className={`text-sm ${msg.ok ? 'text-emerald-700 dark:text-emerald-300' : 'text-red-600 dark:text-red-400'}`}>
          {msg.text}
        </p>
      )}

      {mayIssue && (
        <SoftCard>
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 text-sm mb-3">Issue a discount</h3>

          <div className="relative">
            <Search size={15} className="absolute left-3 top-2.5 text-gray-400" />
            <input
              id="discount-invoice"
              value={picked ? `${picked.invoiceNo} · ${picked.studentName ?? ''}` : search}
              onChange={(e) => { setPickedId(null); setSearch(e.target.value); }}
              placeholder="Search a pupil or invoice number…"
              aria-label="Invoice"
              className={glassFieldClass('pl-9')}
            />
            {!picked && matches.length > 0 && (
              <div className="absolute z-10 mt-1 w-full rounded-lg border-2 border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-900 shadow-lg max-h-64 overflow-y-auto">
                {matches.map((i) => (
                  <button
                    key={i.id}
                    type="button"
                    onClick={() => { setPickedId(i.id); setSearch(''); }}
                    className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 dark:hover:bg-gray-800"
                  >
                    {i.invoiceNo} · {i.studentName}
                    <span className="text-[11px] text-gray-400 ml-2">
                      fee {fmtPula(feeAfterDiscounts(i))} · balance {fmtPula(i.balance)}
                    </span>
                  </button>
                ))}
              </div>
            )}
          </div>

          {picked && (
            <>
              <div className="grid gap-3 sm:grid-cols-[1.4fr_auto_1fr_1fr] mt-3">
                <select
                  id="discount-kind"
                  value={discountKind}
                  onChange={(e) => setDiscountKind(e.target.value as DiscountKind)}
                  aria-label="Type of discount"
                  className={glassFieldClass('')}
                >
                  {KINDS.map((k) => <option key={k} value={k}>{DISCOUNT_KIND_LABELS[k]}</option>)}
                </select>
                <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden" role="group" aria-label="Amount or percentage">
                  {([['amount', 'Pula'], ['percent', '%']] as const).map(([k, label]) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setMode(k)}
                      aria-pressed={mode === k}
                      className={`px-4 py-2 text-sm font-semibold border-r last:border-r-0 border-gray-200 dark:border-gray-600 ${mode === k
                        ? 'bg-[var(--accent-strong)] text-white' : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200'}`}
                    >{label}</button>
                  ))}
                </div>
                <input
                  id="discount-value"
                  value={value}
                  onChange={(e) => setValue(e.target.value.replace(/[^0-9.]/g, ''))}
                  inputMode="decimal"
                  placeholder={mode === 'percent' ? '0 %' : '0.00'}
                  aria-label={mode === 'percent' ? 'Percentage' : 'Amount in pula'}
                  className={glassFieldClass('text-right')}
                />
                <input id="discount-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} aria-label="Date" className={glassFieldClass('')} />
              </div>

              <input
                id="discount-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Why is the school giving this? Kept on the record, not shown to parents."
                aria-label="Reason"
                className={glassFieldClass('mt-3')}
              />

              {/* What it does, before it is done: the figure IS the money given up. */}
              <dl className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 rounded-lg bg-gray-50 dark:bg-gray-800/50 p-3 text-sm tabular-nums">
                <Figure label="Fee on the invoice" value={fmtPula(feeAfterDiscounts(picked))} />
                <Figure label="This discount" value={`− ${fmtPula(amount)}`} />
                <Figure label="Fee after" value={fmtPula(round2(feeAfterDiscounts(picked) - amount))} strong />
                <Figure label="Balance after" value={fmtPula(round2(picked.balance - amount))} strong />
              </dl>

              {(picked.issuedDiscounts ?? []).length > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-2 flex items-start gap-1.5">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  Already discounted: {(picked.issuedDiscounts ?? []).map((d) => `${d.discountNo} (${fmtPula(d.amount)})`).join(', ')}.
                </p>
              )}
              {tooMuch && (
                <p className="text-xs text-red-600 dark:text-red-400 mt-2">
                  The most that can come off this invoice is {fmtPula(room)}, the fee left on it.
                </p>
              )}
              {!tooMuch && toCredit > 0 && (
                <p className="text-xs text-amber-700 dark:text-amber-300 mt-2 flex items-start gap-1.5">
                  <AlertTriangle size={14} className="shrink-0 mt-0.5" />
                  {fmtPula(toCredit)} of this is more than is owed on the invoice, and becomes credit on the account.
                </p>
              )}

              <div className="flex items-center justify-end gap-2 mt-3">
                <button
                  type="button"
                  onClick={submit}
                  disabled={busy || !(amount > 0) || tooMuch || !reasonOk}
                  className={btnPrimary}
                >
                  {busy ? <Loader2 size={14} className="animate-spin" /> : <BadgePercent size={14} />}
                  Issue discount
                </button>
              </div>
            </>
          )}
        </SoftCard>
      )}

      <SoftCard pad="p-0">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-800/50 text-left text-gray-500 dark:text-gray-400">
              <tr>
                <th className="px-4 py-2.5 font-medium">Number</th>
                <th className="px-4 py-2.5 font-medium">Date</th>
                <th className="px-4 py-2.5 font-medium">Pupil</th>
                <th className="px-4 py-2.5 font-medium">Invoice</th>
                <th className="px-4 py-2.5 font-medium">Type</th>
                <th className="px-4 py-2.5 font-medium text-right">Amount</th>
                <th className="px-4 py-2.5 font-medium">Reason</th>
                <th className="px-4 py-2.5 font-medium text-right">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
              {rows.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">
                  No discounts issued here yet.
                </td></tr>
              )}
              {rows.map((a) => {
                const spent = !!a.reversedBy;
                return (
                  <tr key={a.id} className={spent ? 'opacity-60' : ''}>
                    <td className="px-4 py-2.5 font-medium text-gray-800 dark:text-gray-100 whitespace-nowrap">{a.adjustmentNo}</td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">{a.date}</td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{a.studentName ?? a.studentId}</td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300 whitespace-nowrap">{a.invoiceNo ?? '—'}</td>
                    <td className="px-4 py-2.5 text-gray-600 dark:text-gray-300">{a.discountKind ? DISCOUNT_KIND_LABELS[a.discountKind] : '—'}</td>
                    <td className={`px-4 py-2.5 text-right tabular-nums font-semibold ${spent ? 'line-through text-gray-400' : 'text-gray-800 dark:text-gray-100'}`}>
                      {fmtPula(a.amount)}
                    </td>
                    <td className="px-4 py-2.5 text-gray-500 dark:text-gray-400 max-w-xs truncate" title={a.reason}>
                      {a.reason}
                      {a.reversesId && <span className="ml-2 text-[10px] font-bold uppercase text-gray-400">reversal</span>}
                      {spent && <span className="ml-2 text-[10px] font-bold uppercase text-red-500">reversed</span>}
                    </td>
                    <td className="px-4 py-2.5 text-right">
                      {mayIssue && !spent && !a.reversesId && (
                        <button
                          type="button"
                          onClick={() => { setReversing(a); setReverseReason(''); }}
                          className="inline-flex items-center gap-1 text-xs font-semibold text-gray-500 hover:text-red-600"
                        >
                          <Undo2 size={12} /> Reverse
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </SoftCard>

      <p className="text-xs text-gray-500 dark:text-gray-400">
        A discount cannot be edited. To change one, reverse it and issue a new one. Both stay on the record.
        {' '}Discounts given before this screen are in the <a href="#/financeSiblingDiscounts" className="font-semibold underline">earlier record</a>.
      </p>

      {reversing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" role="dialog" aria-modal="true">
          <div className="w-full max-w-md rounded-2xl bg-white dark:bg-gray-800 shadow-2xl border border-gray-200 dark:border-gray-700">
            <div className="px-5 py-4 border-b border-gray-100 dark:border-gray-700 flex items-start gap-3">
              <AlertTriangle size={20} className="text-amber-500 mt-0.5 shrink-0" />
              <div>
                <h3 className="font-bold text-gray-900 dark:text-white">Reverse {reversing.adjustmentNo}?</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                  {fmtPula(reversing.amount)} · {reversing.invoiceNo} · {reversing.studentName}
                </p>
              </div>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm text-gray-700 dark:text-gray-200">
              <p>{fmtPula(reversing.amount)} goes back onto {reversing.invoiceNo}, so the family owes it again.</p>
              <input
                id="discount-reverse-reason"
                autoFocus
                value={reverseReason}
                onChange={(e) => setReverseReason(e.target.value)}
                placeholder="Why is it being reversed?"
                aria-label="Reason for reversing"
                className={glassFieldClass('')}
              />
            </div>
            <div className="px-5 py-3 border-t border-gray-100 dark:border-gray-700 flex items-center justify-end gap-2">
              <button type="button" onClick={() => setReversing(null)} disabled={busy} className={btnGhost}>Cancel</button>
              <button
                type="button"
                onClick={doReverse}
                disabled={busy || reverseReason.trim().length < 3}
                className="px-4 py-2 rounded-lg bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-40 inline-flex items-center gap-1.5"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <Undo2 size={14} />} Reverse it
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Figure({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div>
      <dt className="text-[11px] text-gray-500 dark:text-gray-400">{label}</dt>
      <dd className={strong ? 'font-bold text-gray-900 dark:text-white' : 'text-gray-700 dark:text-gray-200'}>{value}</dd>
    </div>
  );
}
